import { useEffect, useRef, useCallback } from 'react';
import { useNotificationStore } from '../store/notification-store';
import { fetchGitLabNotifications, formatGitLabNotification } from '@shared/git/gitlab-notifications';
const POLL_INTERVAL = 60000; // 1 minute
/**
 * Hook to poll GitLab notifications and add them to the notification store.
 *
 * Usage:
 * ```tsx
 * useGitLabNotifications({
 *   baseUrl: 'https://gitlab.com',
 *   accessToken: 'your-token',
 *   projectId: 12345,
 * });
 * ```
 */
export function useGitLabNotifications(config) {
    const addNotification = useNotificationStore((s) => s.addNotification);
    const notifications = useNotificationStore((s) => s.notifications);
    const seenIds = useRef(new Set());
    const intervalRef = useRef();
    const fetchAndNotify = useCallback(async () => {
        if (!config)
            return;
        const gitlabNotifs = await fetchGitLabNotifications(config);
        for (const notif of gitlabNotifs) {
            if (seenIds.current.has(notif.id))
                continue;
            seenIds.current.add(notif.id);
            // Skip already-read notifications from GitLab
            if (notif.read)
                continue;
            const message = formatGitLabNotification(notif);
            const type = notif.subject_type === 'Pipeline' && notif.pipeline?.status === 'failed'
                ? 'error'
                : notif.subject_type === 'MergeRequest'
                    ? 'info'
                    : 'info';
            addNotification({
                type,
                title: `GitLab: ${notif.subject_type}`,
                message,
                projectId: undefined,
            });
        }
    }, [config, addNotification]);
    // Mark existing notifications as seen on mount
    useEffect(() => {
        if (config) {
            const existingGitlabNotifs = notifications.filter(n => n.title.startsWith('GitLab:'));
            for (const n of existingGitlabNotifs) {
                const idMatch = n.id.match(/notif-(\d+)-(\d+)/);
                if (idMatch) {
                    seenIds.current.add(parseInt(idMatch[1]));
                }
            }
        }
    }, [config, notifications]);
    useEffect(() => {
        if (!config)
            return;
        // Initial fetch
        fetchAndNotify();
        // Set up polling
        intervalRef.current = setInterval(fetchAndNotify, POLL_INTERVAL);
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [config, fetchAndNotify]);
    return { refresh: fetchAndNotify };
}
//# sourceMappingURL=useGitLabNotifications.js.map